<script lang="ts">
    import { urlsBuilderContextToken } from "@/context_tokens";
    import WishlistHeartFilledIcon from "@apps/iloom_customization/components/icons/WishlistHeartFilledIcon.svelte";
    import { ProductQuery } from "@apps/iloom_customization/modules/products/queries/ProductQuery";
    import { WishlistItemEntity } from "@apps/wishlist/entities/WishlistItemEntity";
    import { getTypedContext } from "@lib/svelte-typed-context";
    import { map } from "rxjs";
    import { createEventDispatcher } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Image from "@components/Image/Image.svelte";
    import Money from "@components/Money.svelte";

    export let item: WishlistItemEntity;
    export let productQuery: ProductQuery;

    const urlsBuilder = getTypedContext(urlsBuilderContextToken);

    const dispatch = createEventDispatcher();
    const handleDeleteItem = (payload: {
        wishlistId: number;
        productId: number;
        variantId: number;
    }) => dispatch("deleteItem", payload);

    const productAndVariantInfo$ = productQuery.selectProductAndVariant(
        item.productId,
        item.variantId
    );

    $: selectedVariantId = $productAndVariantInfo$.variant?.id;
    $: imageSrc =
        $productAndVariantInfo$.product?.variants.find(
            (variant) => variant.id === selectedVariantId
        )?.image?.url || $productAndVariantInfo$.product?.featuredImage?.url;

    $: price = $productAndVariantInfo$.variant?.price?.amount;
    $: compareAtPrice = $productAndVariantInfo$.variant?.compareAtPrice?.amount;

    $: getDiscountPercent = () => {
        if (compareAtPrice && price) {
            return `${Math.round(100 - (price * 100) / compareAtPrice)}% off`;
        }
        return null;
    };

    const onChangeVariant = (id: number) => {
        selectedVariantId = id;
    };

    const tags$ = productAndVariantInfo$.pipe(
        map((productInfo) => {
            return productInfo.product?.tags
                .filter((tag) => tag.toLowerCase().includes("series"))
                .map((tag) => tag.replace("series: ", ""));
        })
    );

    const productUrl$ = productAndVariantInfo$.pipe(
        map((productInfo) =>
            urlsBuilder.createProductUrl(productInfo.product?.handle || "")
        )
    );
</script>

<div class="gw-cwl-product-item">
    <a
        href={$productUrl$}
        class="gw-cwl-product-item__image"
        aria-label={$productAndVariantInfo$.product?.title || "Browse product"}
    >
        <Image
            objectFit="contain"
            ratio={1}
            src={urlsBuilder.createProductImageUrl(imageSrc || "")}
            alt={$productAndVariantInfo$.product?.title || ""}
        />
    </a>

    {#if !!$tags$?.length}
        <div class="gw-cwl-product-item__tags">
            {#each $tags$ as tag}
                <span>{tag}</span>
            {/each}
        </div>
    {/if}

    <a href={$productUrl$} class="gw-cwl-product-item__title">
        {$productAndVariantInfo$.product?.title}
    </a>

    <div class="gw-cwl-product-item__price-wrapper">
        {#if price}
            <span class="gw-cwl-product-item__price">
                <Money with_currency amount={price} />
            </span>
        {/if}
        {#if compareAtPrice}
            <span class="gw-cwl-product-item__price-compare">
                <Money with_currency amount={compareAtPrice} />
            </span>
        {/if}
        {#if getDiscountPercent()}
            <span class="gw-cwl-product-item__price-discount">
                {getDiscountPercent()}
            </span>
        {/if}
    </div>

    {#if $productAndVariantInfo$.product?.variants}
        <div class="gw-cwl-product-item__variants">
            {#each $productAndVariantInfo$.product?.variants as variant}
                <input
                    name={$productAndVariantInfo$.product.handle}
                    id={`${variant.title}_${variant.id}`}
                    class="sr-only"
                    type="radio"
                    value={variant.id}
                    on:change={() => onChangeVariant(variant.id)}
                    checked={selectedVariantId === variant.id}
                />
                <label for={`${variant.title}_${variant.id}`}>
                    <span class="sr-only">{variant.title}</span>
                    <img
                        src={variant.image?.url || ""}
                        alt={variant.title}
                        height="auto"
                        width="auto"
                        loading="lazy"
                    />
                </label>
            {/each}
        </div>
    {/if}

    <BaseButton
        class="gw-cwl-product-item__wishlist-btn"
        on:click={() =>
            handleDeleteItem({
                productId: item.productId,
                variantId: item.variantId,
                wishlistId: item.wishlistId,
            })}
    >
        <WishlistHeartFilledIcon />
    </BaseButton>
</div>

<style lang="postcss">
    .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
    }
    .gw-cwl-product-item {
        position: relative;

        &__image {
            display: block;
            margin-bottom: 24px;
        }

        &__tags {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            font-weight: 400;
            font-size: 16px;
            color: #3c3c3c;
            margin-bottom: 8px;
        }

        &__title {
            display: block;
            text-decoration: none;
            font-weight: 500;
            font-size: 20px;
            color: #666;
            margin-bottom: 16px;
        }

        &__price-wrapper {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 10px;
            font-weight: 400;
            font-size: 20px;
        }

        &__price {
            color: #3c3c3c;
        }

        &__price-compare {
            color: #9a9a9a;
            text-decoration: line-through;
        }

        &__price-discount {
            padding: 4px 10px;
            font-family: "Montserrat";
            font-weight: 500;
            font-size: 12px;
            line-height: 1.2;
            color: #fff;
            background-color: #c80a1e;
            border-radius: 99px;
            text-transform: uppercase;
        }

        &__variants {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 20px;

            input {
                display: none;
                &:checked + label::before {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            label {
                position: relative;
                width: 22px;
                height: 22px;
                cursor: pointer;

                &::before {
                    content: "";
                    position: absolute;
                    inset: -2px;
                    opacity: 0;
                    border-radius: 50%;
                    transform: scale(0.75);
                    transition: opacity 0.2s ease-in-out,
                        transform 0.2s ease-in-out;
                    box-shadow: 0 0 0 1px var(--color-grey-06, #666666);
                }
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                border-radius: 50%;
            }
        }

        :global(.gw-cwl-product-item__wishlist-btn) {
            position: absolute;
            top: 20px;
            right: 20px;
            display: flex;
            cursor: pointer;
        }
    }

    @media all and (max-width: 600px) {
        .gw-cwl-product-item {
            &__image {
                margin-bottom: 16px;
            }

            &__tags {
                gap: 4px;
                font-size: 12px;
                margin-bottom: 4px;
            }

            &__title {
                font-size: 14px;
                margin-bottom: 12px;
            }

            &__price-wrapper {
                font-size: 14px;
            }

            &__variants {
                gap: 8px;
                margin-top: 16px;

                label {
                    width: 16px;
                    height: 16px;
                }
            }

            :global(.gw-cwl-product-item__wishlist-btn) {
                top: 10px;
                right: 10px;
            }
        }
    }
</style>
